/* eslint-disable camelcase */
import React, {useEffect, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextareaAutosize,
  FormControl,
  Grid,
  Divider,
  FormHelperText,
  Select,
  ListItemText,
  MenuItem,
  IconButton,
  Popover,
  MenuList,
  Chip,
  Link
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
import {
  addCCMTime,
  getAllCCMUsersOfClinicGroup,
  getCCMLogReasons,
  getCCMPatientDetails,
  updatePrevFollowUpNotesLog
} from '@shared/services/CCMServices';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import WithPermission from '@components/system/WithPermission/WithPermission';
import {
  AppPermissions,
  CCMConstants,
  FOLLOW_UP_NOTES_LOG_STATUS
} from '@shared/constants/Mapping';
import {useSelector} from 'react-redux';
import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import {endOfMonth, format} from 'date-fns';
import CCMEnrollementDialogUtils from '../EnrollCCMDialog/CCMDialog/CCMEnrollementDialogUtils';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import {Controller, useForm} from 'react-hook-form';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import TimePickerWrapper from '@components/system/CustomDateTimePicker/TimePickerWrapper';
import {
  calculateDurationBetweenStartAndStopTime,
  convertTimeToSeconds
} from '../StopTimerDialog/TimerUtils';
import {AlarmAddSharp} from '@material-ui/icons';
import moment from 'moment';
import {useStyles} from './AddCCMTimeDialogStyles';
import {CommunicationService} from 'src/service/CommunicationService';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import Storage from 'src/utils/Storage';
import ConfirmationDialog from '@components/system/ConfirmationDialog/ConfirmationDialog';
import HelperUtils from '@shared/utils/HelperUtils';
import {
  spaceBottomStyle,
  spaceTopStyle
} from 'src/modules/admin/modules/ChronicCareManagement/module-components/EnrollCCMDialog/CCMDialog/CCMEnrollmentDialogStyle';
import CCMShowFullNotesDialog from '../CCMShowFullNotesDialog/CCMShowFullNotesDialog';
import {State} from '@shared/store/reducers/Index';
import MultiSelectDropDownComponent from '@components/system/MultiSelectDropdown/MultiSelectDropdown';
import { capitalizeReason } from '../utils/utils';

interface AddCcmTimeDialogProps {
  open: boolean;
  onClose: () => void;
  patientName: string;
  accountNumber: string;
  clinicId?: number | string;
  patientId: number | undefined;
  ccmPatientId: number | undefined;
  resetPagesInGrid?: () => void;
}

type StateProps = {
  consentType: string;
  consentFile: File | null | any;
  notes: string;
  BillingMonth: string;
  buttonLoader: boolean;
  reasonList: Array<any>;
  // reason: string;
  // reasonId: number | undefined;
  prevFollowUpNotes: string;
  prevFollowUpNotesLogId: number | undefined;
  showPreviousFollowUpNoteConfirmDialog: boolean;
  moreDetails: boolean;
  selectedReasons: any[];
};
const AddCcmTimeDialog: React.FC<AddCcmTimeDialogProps> = ({
  open,
  onClose,
  patientName,
  accountNumber,
  ccmPatientId,
  clinicId,
  patientId,
  resetPagesInGrid
}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const {enqueueSnackbar} = useSnackbar();
  const timerInfo = useSelector((stateParam: any) => stateParam.ccmTimer);
  const [selectedAssignee, setSelectedAssignee] = React.useState(''); // To manage selected assignee
  const [ccmUsers, setCcmUsers] = useState([]);
  const [stateValues, setState] = useState<StateProps>({
    consentType: '',
    consentFile: null,
    notes: '',
    reasonList: [],
    BillingMonth: CCMEnrollementDialogUtils.CurrentMonthAndYear(),
    buttonLoader: false,
    prevFollowUpNotes: '',
    prevFollowUpNotesLogId: undefined,
    showPreviousFollowUpNoteConfirmDialog: false,
    moreDetails: false,
    selectedReasons: []
  });
  const {
    consentType,
    notes,
    BillingMonth,
    buttonLoader,
    reasonList,
    prevFollowUpNotes,
    prevFollowUpNotesLogId,
    showPreviousFollowUpNoteConfirmDialog,
    moreDetails,
    selectedReasons
  } = stateValues;
  const [reasonNameChips, setReasonNameChips] = useState<string[]>([]);

  const {
    control,
    errors,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    watch
  } = useForm({
    mode: 'all',
    resolver: async (values: any) => {
      const error: any = {};
      // destructure the field values and map them to validate
      const {reason_name: reasonName} = values;
      // Check if `selectedReasons` is an array and has at least one element
      if (!Array.isArray(selectedReasons) || selectedReasons.length === 0) {
        error.activityReasons = t('CCM.CCM_REASON');
      }
      // Validate reason_name if "Specify Custom Task" is selected
      if (
        Array.isArray(selectedReasons) &&
        selectedReasons.includes(CCMConstants.SPECIFY_CUSTOM_TASK)
      ) {
        const newReason: string = reasonName?.trim();
        // Check if no chips exist and reason_name is empty
        if (reasonNameChips.length === 0 && !newReason) {
          error.reason_name = t('CCM.CCM_REASON_NAME');
        }

        // Check if reason_name already exists in chips
        if (newReason && reasonNameChips.includes(newReason)) {
          error.reason_name = `"${newReason}" is already added.`;
        }
      }

      if (values?.follow_up_notes_due_date) {
        if (!moment(values?.follow_up_notes_due_date).isValid()) {
          error.follow_up_notes_due_date = 'CCM.ADD_TIME.INVALID_DATE';
        } else if (
          moment(values?.follow_up_notes_due_date).isBefore(
            moment().startOf('day')
          )
        ) {
          error.follow_up_notes_due_date = 'CCM.ADD_TIME.INVALID_DUE_DATE';
        }
      }

      if (!values?.start_time) {
        error.start_time = t('CCM.START_TIME_IS_REQUIRED');
      }
      if (!values?.stop_time) {
        error.stop_time = t('CCM.STOP_TIME_IS_REQUIRED');
      }
      if (values?.start_time && !values.start_time?.isValid()) {
        error.start_time = t('CCM.INVALID_TIME_FORMAT');
      }
      if (values?.stop_time && !values.stop_time?.isValid()) {
        error.stop_time = t('CCM.INVALID_TIME_FORMAT');
      }
      if (
        values?.start_time &&
        values?.stop_time &&
        values.start_time?.isValid?.() &&
        values.stop_time?.isValid?.()
      ) {
        const startTime = moment(values?.start_time);
        const stopTime = moment(values?.stop_time);
        if (stopTime.diff(startTime, 'seconds') === 0) {
          error.stop_time = t('CCM.FIST_TIME_AND_END_TIME_MUST_BE_DIFFERENT');
        } else if (stopTime.isBefore(startTime)) {
          error.stop_time = t('CCM.INVALID_TIME_RANGE');
        }
      }
      return {
        values,
        errors: error
      };
    },
    defaultValues: {
      follow_up_notes_due_date: null
    }
  });

  const [startDate, setStartDate] = useState<Date | any>(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [stopDate, setStopDate] = useState<Date | any>(
    format(new Date(), 'yyyy-MM-dd')
  );

  const [selectedTime, setSelectedTime] = useState<{
    selectedStartTime: null | any;
    selectedStopTime: null | any;
  }>({
    selectedStartTime: null,
    selectedStopTime: null
  });
  const {selectedStartTime, selectedStopTime} = selectedTime;
  const [minuteState, setMinuteState] = useState<{
    enteredMinute: string;
    enteredMinuteError: string;
  }>({
    enteredMinute: '',
    enteredMinuteError: ''
  });

  const handleStartTimeChange = (time: any) => {
    // Remove the seconds, because the library preserve the seconds.
    let formattedTime = time;
    if (formattedTime?.isValid?.()) {
      formattedTime = time.seconds(0);
    }
    setSelectedTime((prevState: any) => ({
      ...prevState,
      selectedStartTime: formattedTime ?? null
    }));
    setValue('start_time', formattedTime, {
      shouldValidate: true
    });
  };

  const handleStopTimeChange = (time: any) => {
    // Remove the seconds, because the library preserve the seconds.
    let formattedTime = time;
    if (formattedTime?.isValid?.()) {
      formattedTime = time.seconds(0);
    }
    setSelectedTime((prevState: any) => ({
      ...prevState,
      selectedStopTime: formattedTime ?? null
    }));
    setValue('stop_time', formattedTime, {
      shouldValidate: true
    });
  };

  const groupId: any = useSelector((state: any) => state.auth.groupId);

  const init = async () => {
    try {
      setState(prevState => ({
        ...prevState,
        buttonLoader: true
      }));
      const res = await getCCMLogReasons(groupId);
      // Capitalize reasons once when we receive the list so UI and payload use the same casing
      const capitalizedReasonList =
        Array.isArray(res.data) && res.data.length
          ? res.data.map((item: any) => ({
              ...item,
              reason:
                typeof item.reason === 'string'
                  ? capitalizeReason(item.reason)
                  : item.reason
            }))
          : [];
      if (ccmPatientId) {
        const ccmPatientRes = await getCCMPatientDetails(
          groupId,
          ccmPatientId,
          {
            clinic_id: clinicId ?? timerInfo.clinicId
          }
        );
        if (ccmPatientRes?.data) {
          setState(p => ({
            ...p,
            prevFollowUpNotes: ccmPatientRes?.data?.follow_up_notes,
            prevFollowUpNotesLogId: ccmPatientRes?.data?.follow_up_note_log_id
          }));
        }
      }
      setState(prevState => ({
        ...prevState,
        reasonList: capitalizedReasonList,
        buttonLoader: false
      }));
    } catch (e) {
      setState(prevState => ({
        ...prevState,
        buttonLoader: false
      }));
      enqueueSnackbar(e && SnackBarConfig('e'));
    }
  };

  useEffect(() => {
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.persist();
    setState(prevState => ({
      ...prevState,
      notes: event.target.value
    }));
  };

  const handleAddCCMTime = async (data: any) => {
    const duration = calculateDurationBetweenStartAndStopTime(
      selectedStartTime,
      selectedStopTime
    );
    const utcStartDatetime = moment(selectedStartTime).utc().format();
    const utcStopDatetime = moment(selectedStopTime).utc().format();
    try {
      setState(prevState => ({
        ...prevState,
        buttonLoader: true
      }));

      let trimmedNotes = null;
      if (notes && typeof notes === 'string' && notes.trim()) {
        trimmedNotes = notes.trim();
      }
      let trimmedFollowUpNotes = null;
      if (
        data?.follow_up_notes &&
        typeof data.follow_up_notes === 'string' &&
        data.follow_up_notes.trim()
      ) {
        trimmedFollowUpNotes = data.follow_up_notes.trim();
      }

      // ** Creating the Activiy Reasons multiselect dropdown payload formation **//
      // ** Find the matching items from reasonList based on selectedReasons --> START */
      // Step 1: Filter selected reasons from reasonList because SelectedReasons array has only string value doesnt have id's
      const filteredReasons: any = reasonList.filter((value: any) =>
        selectedReasons.includes(value.reason)
      );
      // Step 2: Exclude "Specify Custom Task" from the filtered list--
      // because I dont want to send reason_id : 5 which is exclusively added for specify custom task in multi select dropdown
      const filteredReasonsWithoutCustomTask: any = filteredReasons.filter(
        (value: any) => value.reason !== CCMConstants.SPECIFY_CUSTOM_TASK
      );
      // Step 3: Map the remaining reasons to their IDs
      const reasonIds: any = filteredReasonsWithoutCustomTask.map(
        (resn: any) => ({
          reason_id: resn.id
        })
      );

      // Step 4: Map reasonNameChips to reason_name
      const reasonNames: any = reasonNameChips.map((reasonName: string) => ({
        reason_name: reasonName
      }));

      // Step 5: Combine reasonIds and reasonNames into optedReasons Array
      const optedReasons = reasonIds.concat(reasonNames);

      const payload: any = {
        start_time: utcStartDatetime,
        stop_time: utcStopDatetime,
        time_spent: convertTimeToSeconds(duration),
        notes: trimmedNotes,
        log_type: 'messagae', // TODO: update this log_type value
        follow_up_notes: trimmedFollowUpNotes,
        complete_previous_note: data.complete_previous_note ?? false,
        follow_up_notes_due_date: data?.follow_up_notes_due_date
          ? moment(data?.follow_up_notes_due_date).format('YYYY-MM-DD')
          : null,
        follow_up_notes_assignee: data?.follow_up_notes_assignee,
        activity_reason: optedReasons
      };

      const options: Record<string, any> = {};
      if (clinicId) {
        options.clinic_id = clinicId;
      }
      const res = await addCCMTime(groupId, ccmPatientId, payload, options);
      if (res) {
        setState(prevState => ({
          ...prevState,
          buttonLoader: false
        }));
        enqueueSnackbar(t('CCM.ADD_TIME_CCM'), SnackBarConfig('s'));
        if (typeof resetPagesInGrid === 'function') {
          resetPagesInGrid();
        }
      }
      CommunicationService.sendEvent({
        action: 'ADDED_CCM_TIME',
        data: null
      });
      onClose();
    } catch (error: any) {
      setState(prevState => ({
        ...prevState,
        buttonLoader: false
      }));
      enqueueSnackbar(error && error?.message, SnackBarConfig('e'));
    } finally {
      setState(prevState => ({
        ...prevState,
        buttonLoader: false
      }));
    }
  };

  const renderStopTimerNoteTextArea = () => {
    return (
      <div>
        <TextareaAutosize
          rowsMax={10}
          rows={4}
          aria-label="maximum height"
          placeholder={t('CCM.ENTER_SOME_NOTES')}
          className={`app-text-area ${classes.TextareaAutosize}`}
          onChange={handleNotesChange}
          value={notes}
        />
      </div>
    );
  };

  const renderFollowUpNotesTextArea = () => {
    return (
      <Controller
        name="follow_up_notes"
        control={control}
        render={inputProps => (
          <TextareaAutosize
            rowsMax={10}
            rows={4}
            aria-label="maximum height"
            placeholder={t('CCM.ENTER_FOLLOW_UP_NOTES')}
            className={`app-text-area ${classes.TextareaAutosize}`}
            {...inputProps}
          />
        )}
      />
    );
  };

  const renderDialogTitle = () => {
    return (
      <div>
        {t('CCM.ADD_TIME.ADD_TIME_FOR_PATIENT', {
          patientName: patientName
        })}
        <strong>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</strong>
        {`${accountNumber})`}
      </div>
    );
  };
  const selectStartDate = (dateParam: any) => {
    const formattedDate = dateParam
      ? format(dateParam.toDate(), 'yyyy-MM-dd')
      : null;
    setStartDate(formattedDate);
  };

  const renderMarkPrevFollowUpNoteConfirmDialog = () => {
    return (
      <ConfirmationDialog
        open={showPreviousFollowUpNoteConfirmDialog}
        title={t('CCM.CCM_MARK_PREV_FOLLOW_UP_NOTES_DIALOG_TITLE')}
        description={t('CCM.CCM_MARK_PREV_FOLLOW_UP_NOTES_DIALOG_DESCRIPTION')}
        onClose={response => {
          const data = getValues();
          if (response === 'confirm') {
            data.complete_previous_note = true;
          }
          handleAddCCMTime(data);
          setState(p => ({
            ...p,
            showPreviousFollowUpNoteConfirmDialog: false
          }));
        }}
        cancelButtonText={t('COMMON.NO')}
        confirmButtonText={t('COMMON.YES')}
      />
    );
  };

  const selectStopDate = (dateParam: any) => {
    const formattedDate = dateParam
      ? format(dateParam.toDate(), 'yyyy-MM-dd')
      : null;
    setStopDate(formattedDate);
  };

  // Date selection for enrolling into CCM in the current month function start //

  const renderCCMStartDateField = (value: any, handleSelectDate: any) => {
    const currentDate = new Date();
    const startOfDate = currentDate;
    const endDate = endOfMonth(currentDate);

    return (
      <DateTimePickerWrapper
        fullWidth
        clearable
        minDate={startOfDate}
        maxDate={endDate}
        views={['month', 'date', 'year']}
        format={'MM/DD/YYYY'}
        value={value}
        placeholder={t(
          'PATIENT_DETAILS_DRAWER.CCM_MODULE.SELECT_MONTH_AND_YEAR'
        )}
        onChange={handleSelectDate}
        disabled
      />
    );
  };

  // const handleSelectReason = (item: any) => {
  //   setState(prevState => ({
  //     ...prevState,
  //     reason: item.reason,
  //     reasonId: item.id
  //   }));
  // };
  const [
    minuteMenuAnchorEl,
    setMinuteMenuAnchorEl
  ] = useState<HTMLElement | null>(null);
  const handleOpenMinuteMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMinuteMenuAnchorEl(event.currentTarget);
  };

  const formattedDuration = calculateDurationBetweenStartAndStopTime(
    selectedStartTime,
    selectedStopTime,
    true
  );

  const handleCloseMinuteMenu = () => {
    setMinuteMenuAnchorEl(null);
    setMinuteState({
      enteredMinute: '',
      enteredMinuteError: ''
    });
  };

  const handleSelectMinute = (minute: number) => {
    if (
      minute &&
      selectedTime.selectedStartTime &&
      selectedTime.selectedStartTime?.isValid?.()
    ) {
      const updatedEndTime = moment(selectedTime.selectedStartTime).add(
        minute,
        'minutes'
      );
      handleStopTimeChange(updatedEndTime);
    }
    handleCloseMinuteMenu();
  };

  useEffect(() => {
    const numberRegEx = /^\d+$/;
    // Update end time if entered minute is a valid number
    if (numberRegEx.test(minuteState.enteredMinute)) {
      if (minuteState.enteredMinute && selectedTime.selectedStartTime) {
        // update the end time
        const updatedEndTime = moment(selectedTime.selectedStartTime).add(
          parseInt(minuteState.enteredMinute),
          'minutes'
        );
        if (!updatedEndTime.isSame(moment(), 'day')) {
          // set the error message
          setMinuteState(p => ({
            ...p,
            enteredMinuteError: t('CCM.END_TIME_EXCEEDS_CURRENT_DATE')
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minuteState.enteredMinute]);

  // When ever start time changes validate the end time too
  useEffect(() => {
    if (selectedTime.selectedStartTime && selectedTime.selectedStopTime) {
      trigger('stop_time');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTime.selectedStartTime]);

  const handleMinuteChange: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = e => {
    e.persist();
    const numberRegEx = /^\d+$/;

    if (!numberRegEx.test(e.target.value)) {
      setMinuteState(p => ({
        ...p,
        enteredMinute: e.target.value,
        enteredMinuteError: t('CCM.ENTER_VALID_MINUTE')
      }));
    } else {
      setMinuteState(p => ({
        ...p,
        enteredMinute: e.target.value,
        enteredMinuteError: ''
      }));
    }
  };
  // Date selection for enrolling into CCM in the current month function end //
  const renderMinuteOptions = () => {
    const minutes = Array.from(Array(20), (_, index) => index + 1);
    const minuteGroups = [];

    while (minutes.length > 0) {
      minuteGroups.push(minutes.splice(0, 5));
    }

    return (
      <div className={classes.minutePopUp}>
        <div>
          <p className={classes.minutePopUpHeader}>
            {t('CCM.SELECT_MINUTES_FROM_PALETTE')}
          </p>
          <MenuList className={classes.minutesPaletteWrapper}>
            {minuteGroups.map(group =>
              group.map(minute => (
                <MenuItem
                  key={minute}
                  value={minute}
                  onClick={() => handleSelectMinute(minute)}
                  className={classes.minutePaletteItem}>
                  {minute}
                </MenuItem>
              ))
            )}
          </MenuList>
        </div>
        <p className={classes.or}>{t('CCM.OR')}</p>
        <div>
          <p className={classes.minutePopUpHeader}>{t('CCM.ENTER_MINUTE')}</p>
          <FormControl
            fullWidth={true}
            error={!!minuteState?.enteredMinuteError}
            className={classes.formControl}>
            <StyledOutlinedInput
              onChange={handleMinuteChange}
              value={minuteState.enteredMinute}
              type="text"
            />
            {minuteState.enteredMinuteError && (
              <FormHelperText>{minuteState.enteredMinuteError}</FormHelperText>
            )}
          </FormControl>
        </div>
        <div className={classes.minutesButtonsWrapper}>
          <Button
            variant={'contained'}
            onClick={() => {
              if (!minuteState.enteredMinuteError) {
                const updatedEndTime = moment(
                  selectedTime.selectedStartTime
                ).add(minuteState.enteredMinute, 'minutes');
                handleStopTimeChange(updatedEndTime);
                handleCloseMinuteMenu();
              }
            }}
            color="primary">
            {t('COMMON.OK')}
          </Button>
          <Button
            onClick={handleCloseMinuteMenu}
            className={classes.cancelButton}>
            {t('COMMON.CANCEL')}
          </Button>
        </div>
      </div>
    );
  };

  // ** Multi selected dropdown implementation start **//
  useEffect(() => {
    // Check if selectedReasons is empty or not
    if (selectedReasons.length === 0) {
      // If empty, set validation error for 'activityReasons'
      setValue('activityReasons', [], {shouldValidate: true});
    } else {
      // If not empty, clear validation error for 'activityReasons'
      setValue('activityReasons', selectedReasons, {shouldValidate: true});
    }
    if (reasonNameChips?.length > 0) {
      setValue('reason_name', '', {
        shouldValidate: true
      });
    }
  }, [selectedReasons, setValue, reasonNameChips]);

  const handleItemChange = (event: any, clinic: any) => {
    // handler for item change
  };

  const onClickCalled = (event: any, clinic: any) => {
    // Clone the current selectedReasons array
    const activityReasons = [...selectedReasons];
    // Check if the activityReasons is already selected
    const reasonIndex = activityReasons.indexOf(clinic.reason);
    if (reasonIndex > -1) {
      // If activityReasons is already selected, remove it
      activityReasons.splice(reasonIndex, 1);
    } else {
      // If activityReasons is not selected, add it
      activityReasons.push(clinic?.reason);
    }
    // Update the state with the modified reasons array
    setState(prevState => ({
      ...prevState,
      selectedReasons: activityReasons
    }));
  };

  const handleAddChip = (chipValue: string): void => {
    const newReason = chipValue.trim();
    if (newReason && !reasonNameChips.includes(newReason)) {
      setReasonNameChips(prevChips => [...prevChips, newReason]); // Add new chip to array
    }
  };

  const handleDeleteChip = (chipToDelete: string): void => {
    setReasonNameChips((prevChips: any) =>
      prevChips.filter((chip: any) => chip !== chipToDelete)
    );
  };

  const renderPreDefinedActivityReasons = () => {
    return (
      <>
        <div className={classes.marginBottom}>{t('CCM.REASON')}</div>
        <FormControl
          fullWidth={true}
          error={!!errors?.activityReasons}
          variant="outlined"
          className="select_clinic"
          style={{margin: 0, padding: 0}}>
          <Controller
            name="activityReasons"
            control={control}
            // defaultValue={[]}
            render={field => (
              <MultiSelectDropDownComponent
                selectedValues={selectedReasons}
                data={reasonList}
                onClickCalled={(event: any, clinic: any) => {
                  onClickCalled(event, clinic);
                  field.onChange(selectedReasons); // Update the field value
                }}
                handleItemChange={(event: any, clinic: any) => {
                  handleItemChange(event, clinic);
                }}
                clearFilter={() => {
                  setState((p: any) => ({
                    ...p,
                    selectedReasons: []
                  }));
                  field.onChange([]);
                }}
                primaryLabel={
                  selectedReasons.length
                    ? selectedReasons
                    : [t('Select the activity/reason')]
                }
                headerLabel={t('Activity/Reason')}
                objKey={'reason'}
              />
            )}
          />
          {errors?.activityReasons && (
            <FormHelperText>{t(errors.activityReasons)}</FormHelperText>
          )}
        </FormControl>
      </>
    );
  };

  const renderSpecifyCustomTaskField = () => {
    return (
      <div>
        {selectedReasons.includes(CCMConstants.SPECIFY_CUSTOM_TASK) && (
          <div style={{marginTop: '1rem'}}>
            <div style={{...spaceBottomStyle, ...spaceTopStyle}}>
              {t('CCM.STOP_TIMER.REASON_NAME')}*
            </div>
            <FormControl
              fullWidth={true}
              variant="outlined"
              error={!!errors?.reason_name}
              style={{paddingBottom: '0'}}>
              <Controller
                name="reason_name"
                control={control}
                defaultValue=""
                render={({value, onChange}) => (
                  <div className={classes.reasonsInputField}>
                    <StyledOutlinedInput
                      style={{flex: 1}}
                      value={value || ''}
                      onChange={event => {
                        onChange(event.target.value);
                      }}
                      placeholder={t('CCM.STOP_TIMER.ENTER_REASON_NAME')}
                      autoComplete="on"
                    />
                    <Link
                      component="button"
                      // variant="body2"
                      className={classes.addButton}
                      onClick={(event: any) => {
                        event.preventDefault();
                        handleAddChip(value); // Add chip
                        onChange(''); // Clear input field
                      }}
                      disabled={!value?.trim()}>
                      {t('Add')}
                    </Link>
                  </div>
                )}
              />
              {errors?.reason_name && (
                <FormHelperText>{t(errors.reason_name)}</FormHelperText>
              )}
            </FormControl>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div>
        <Grid container direction="row" spacing={2}>
          <Grid item direction="row" xs={12} md={6}>
            <div className={classes.addCCMTimeBillingTitleWrapper}>
              <div className={classes.addCCMTimeBillingHeader}>
                {`${t('CCM.BILLING_MONTH')}:`}
              </div>
              <div className={classes.addCCMTimeBillingSubHeader}>
                {BillingMonth}
              </div>
            </div>
          </Grid>
        </Grid>
        <div className={classes.marginBottom}>
          <Divider />
        </div>
        <Grid container>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} md={6} className={classes.gridPadding}>
              <div className={classes.marginBottom}>{t('CCM.START_DATE')}</div>
              {renderCCMStartDateField(startDate, selectStartDate)}
            </Grid>
            <Grid item xs={12} md={6} className={classes.gridPadding}>
              <div className={classes.marginBottom}>
                {t('CCM.ADD_TIME.START_TIME')}
              </div>
              <FormControl
                fullWidth={true}
                error={!!errors?.start_time}
                className={classes.formControl}>
                <Controller
                  control={control}
                  name="start_time"
                  error={!!errors?.start_time}
                  render={({onBlur}) => (
                    <TimePickerWrapper
                      fullWidth
                      value={selectedStartTime}
                      onChange={handleStartTimeChange}
                      placeholder="Select Time"
                      onBlur={onBlur}
                    />
                  )}
                />
                {errors?.start_time && (
                  <FormHelperText>{errors.start_time}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} className={classes.gridPadding}>
              <div className={classes.marginBottom}>
                {t('CCM.ADD_TIME.END_DATE')}
              </div>
              {renderCCMStartDateField(stopDate, selectStopDate)}
            </Grid>
            <Grid item className={classes.gridPadding} xs={12} md={6}>
              <div>
                <div className={classes.marginBottom}>
                  {t('CCM.ADD_TIME.END_TIME')}
                </div>
                <div className={classes.stopTimeInputWrapper}>
                  <FormControl
                    fullWidth={true}
                    error={!!errors?.stop_time}
                    className={classes.formControl}>
                    <Controller
                      control={control}
                      name="stop_time"
                      error={!!errors?.stop_time}
                      render={({onBlur}) => (
                        <TimePickerWrapper
                          fullWidth
                          value={selectedStopTime}
                          onChange={handleStopTimeChange}
                          placeholder="Select Time"
                          onBlur={onBlur}
                        />
                      )}
                    />
                    {errors?.stop_time && (
                      <FormHelperText>{errors.stop_time}</FormHelperText>
                    )}
                  </FormControl>
                  <div className={classes.minuteIconWrapper}>
                    <IconButton
                      disabled={!selectedTime.selectedStartTime}
                      aria-label="delete"
                      onClick={handleOpenMinuteMenu}
                      className={classes.minuteIcon}>
                      <AlarmAddSharp />
                    </IconButton>
                    <Popover
                      anchorEl={minuteMenuAnchorEl}
                      open={Boolean(minuteMenuAnchorEl)}
                      onClose={handleCloseMinuteMenu}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                      }}
                      className={classes.minutesMenu}>
                      {renderMinuteOptions()}
                    </Popover>
                  </div>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={6} className={classes.gridPadding}>
              {renderPreDefinedActivityReasons()}
            </Grid>
            <Grid item xs={12} md={6}>
              <div className={classes.marginBottom}>
                {t('CCM.STOP_TIMER.DURATION')}
              </div>
              <div>{formattedDuration ? formattedDuration : '-'}</div>
            </Grid>
          </Grid>
          {/** To make the Reason name input field take full width added the code here */}
          <Grid container>
            <Grid item xs={12}>
              {renderSpecifyCustomTaskField()}
              {/* Render Reason name Chips */}
              <div>
                {reasonNameChips.map((chip: any, index: number) => (
                  <Chip
                    key={index}
                    label={chip}
                    onDelete={() => {
                      handleDeleteChip(chip);
                    }}
                    style={{margin: '0.2rem'}}
                  />
                ))}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  };

  const handleSaveClick = async (data: any): Promise<void> => {
    if (
      (data?.follow_up_notes ||
        data.follow_up_notes_due_date ||
        data.follow_up_notes_assignee) &&
      prevFollowUpNotesLogId
    ) {
      setState(p => ({...p, showPreviousFollowUpNoteConfirmDialog: true}));
    } else {
      handleAddCCMTime(data);
    }
  };

  const renderButtons = () => {
    return (
      <div>
        <Button onClick={onClose}>{t('COMMON.CANCEL')}</Button>
        <Button
          disabled={
            buttonLoader ||
            (selectedReasons?.includes(CCMConstants.SPECIFY_CUSTOM_TASK) &&
              reasonNameChips?.length === 0)
          }
          type="submit"
          variant={'contained'}
          onClick={handleSubmit(handleSaveClick)}
          color="primary">
          {buttonLoader ? <ButtonLoader color={'white'} /> : t('COMMON.SAVE')}
        </Button>
      </div>
    );
  };

  const markPrevFollowUpNotesAsDone = async () => {
    try {
      const payload = {
        status: FOLLOW_UP_NOTES_LOG_STATUS.COMPLETED
      };
      if (groupId && ccmPatientId && prevFollowUpNotesLogId) {
        await updatePrevFollowUpNotesLog(
          groupId,
          ccmPatientId,
          prevFollowUpNotesLogId,
          payload
        );
        setState(p => ({
          ...p,
          prevFollowUpNotes: '',
          prevFollowUpNotesLogId: undefined
        }));
        const timerDataStr = Storage.getItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA
        );
        if (timerDataStr) {
          const parsedTimerData = JSON.parse(timerDataStr);
          if (
            parsedTimerData &&
            parsedTimerData?.ccmPanelData &&
            parsedTimerData?.ccmPanelData?.prevFollowUpNotesLogId ==
              prevFollowUpNotesLogId &&
            parsedTimerData?.ccmPanelData?.ccmPatientId == ccmPatientId
          ) {
            Storage.setItem(
              CCMConstants.STORAGE_CCM_TIMER_DATA,
              JSON.stringify({
                ...parsedTimerData,
                ccmPanelData: {
                  ...parsedTimerData.ccmPanelData,
                  prevFollowUpNotes: '',
                  prevFollowUpNotesLogId: undefined
                }
              })
            );
            CommunicationService.sendEvent({
              action: 'CCM_PANEL_DATA_UPDATED',
              data: null
            });
          }
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err && err.message, SnackBarConfig('e'));
    }
  };

  const renderPrevFollowUpNotes = () => {
    const maxCharacters = 15;
    return (
      <div className={`${classes.prevFollowUpNotesWrapper}`}>
        <p className={`${classes.header}`}>
          {t('CCM.PREVIOUS_FOLLOW_UP_NOTES')}
        </p>
        <div className={`${classes.followUpNotesAndMarkAsDoneWrapper}`}>
          {prevFollowUpNotes.length <= maxCharacters ? (
            <p>{prevFollowUpNotes}</p>
          ) : (
            <p>
              {prevFollowUpNotes.slice(0, maxCharacters)}
              {'... '}
              <span
                onClick={() => {
                  setState(p => ({
                    ...p,
                    moreDetails: true
                  }));
                }}
                className="link-text-style"
                style={{cursor: 'pointer'}}>
                {t('CCM.MORE_DETAILS')}
              </span>
            </p>
          )}
          <div
            onClick={markPrevFollowUpNotesAsDone}
            className={`link-text-style ${classes.markFollowUpNoteAsDoneWrapper}`}>
            <CheckCircleOutlineIcon />
            <p>{t('CCM.MARK_PREV_FOLLOW_UP_NOTE_AS_DONE')}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderMoreNotesDetails = () => {
    return (
      <CCMShowFullNotesDialog
        open={moreDetails}
        onClose={() => {
          setState(p => ({
            ...p,
            moreDetails: false
          }));
        }}
        notes={prevFollowUpNotes}
        title={t('CCM.PREVIOUS_FOLLOW_UP_NOTES')}
      />
    );
  };
  const renderFollowUpNotesDueDate = () => {
    return (
      <div>
        <div
          className={classes.followUpNotesTitle}
          style={{...spaceBottomStyle}}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_DUE_DATE')}
        </div>
        <FormControl
          fullWidth={true}
          variant="outlined"
          error={errors?.follow_up_notes_due_date}
          className={classes.formControlStyle}>
          <Controller
            control={control}
            name={'follow_up_notes_due_date'}
            error={errors?.follow_up_notes_due_date}
            render={({onChange, ...inputProps}) => (
              <DateTimePickerWrapper
                margin={'dense'}
                style={{width: '100%'}}
                type={'string'}
                minDate={new Date()}
                autoComplete="on"
                {...inputProps}
                placeholder={t('CCM.DUE_DATE_PLACEHOLDER')}
                onChange={(date, value) => {
                  setValue('follow_up_notes_due_date', value, {
                    shouldValidate: true
                  });
                }}
              />
            )}
            defaultValue={null}
          />
          {errors?.follow_up_notes_due_date && (
            <FormHelperText>
              {t(errors.follow_up_notes_due_date)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };
  const handleSelectAssignee = (item: any) => {
    setSelectedAssignee(
      HelperUtils.commaSeparatedFullName(item?.first_name, item?.last_name)
    );
  };
  const hasAssignee = !!watch('follow_up_notes_assignee');
  useEffect(() => {
    if (!hasAssignee) {
      setSelectedAssignee('');
    }
  }, [hasAssignee]);

  const renderFollowUpNotesAssignee = () => {
    return (
      <div style={{flex: 1}}>
        <div
          className={classes.followUpNotesTitle}
          style={{...spaceBottomStyle}}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_ASSIGNEE')}
        </div>
        <FormControl
          fullWidth={true}
          error={!!errors?.follow_up_notes_assignee}
          variant="outlined"
          className={['select_clinic', classes.formControlStyle].join(' ')}>
          <Controller
            id="follow_up_notes_assignee"
            control={control}
            name="follow_up_notes_assignee"
            error={!!errors?.follow_up_notes_assignee}
            render={({onBlur}) => (
              <Select
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                margin="dense"
                value={selectedAssignee || 'Select Assignee'} // Set selected value in the dropdown
                variant="outlined"
                renderValue={selected => selected as string}
                fullWidth={true}
                onBlur={onBlur}
                name="follow_up_notes_assignee"
                IconComponent={KeyboardArrowDownIcon}>
                <div>
                  {ccmUsers.map((item: any) => (
                    <MenuItem
                      key={item?.id}
                      value={HelperUtils.commaSeparatedFullName(
                        item?.first_name,
                        item?.last_name
                      )}
                      onClick={() => {
                        handleSelectAssignee(item);
                        setValue('follow_up_notes_assignee', item.id, {
                          shouldValidate: true
                        });
                      }}>
                      <ListItemText
                        primary={HelperUtils.commaSeparatedFullName(
                          item?.first_name,
                          item?.last_name
                        )}
                      />
                    </MenuItem>
                  ))}
                </div>
              </Select>
            )}
            defaultValue={null}
          />
          {errors?.follow_up_notes_assignee && (
            <FormHelperText>
              {t(errors.follow_up_notes_assignee)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };

  const renderFollowUpNotesDueDateAndAssignedUserDetails = () => {
    return (
      <>
        <Grid item xs={12} sm={6}>
          {renderFollowUpNotesDueDate()}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderFollowUpNotesAssignee()}
        </Grid>
      </>
    );
  };
  // TODO: Replace this with the redux value once the patient shared redux state issue is fixed
  const {userPermissions} = useSelector((state: State) => state.auth);
  let patientShared = false;
  if (groupId) {
    patientShared =
      userPermissions?.[groupId]?.['group']?.[
        AppPermissions.PATIENT_MENU_PERMISSIONS.PATIENT_SHARED
      ] === true;
  }

  const fetchCCMUsersList = async () => {
    try {
      const options: {clinic_id?: number | string} = {};
      if (!patientShared) {
        options['clinic_id'] = clinicId;
      }
      const response = await getAllCCMUsersOfClinicGroup(groupId, options);
      setCcmUsers(response?.data?.rows);
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
      throw error;
    }
  };
  useEffect(() => {
    fetchCCMUsersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderDialogContent = () => {
    return (
      <div>
        {renderContent()}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {prevFollowUpNotes && renderPrevFollowUpNotes()}
          </Grid>
          <Grid item xs={12}>
            <div className={classes.ccmNotesWrapper}>
              {consentType === t('CCM.VERBAL')
                ? `${t('CCM.NOTES')}*`
                : `${t('CCM.NOTES')}`}
            </div>
            {renderStopTimerNoteTextArea()}
          </Grid>
          <Grid item xs={12}>
            <div className={classes.ccmNotesWrapper}>
              {t('CCM.FOLLOW_UP_NOTES')}
            </div>
            {renderFollowUpNotesTextArea()}
          </Grid>
          <Grid container item xs={12} spacing={2}>
            {renderFollowUpNotesDueDateAndAssignedUserDetails()}
          </Grid>
        </Grid>
      </div>
    );
  };
  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth={true}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      open={open}
      onClose={onClose}
      aria-labelledby="enrollment-dialog-title">
      <DialogTitle disableTypography id="enrollment-dialog-title">
        {renderDialogTitle()}
      </DialogTitle>
      <DialogContent dividers>{renderDialogContent()}</DialogContent>
      <DialogActions className={classes.addCCMTimeDialogActions}>
        {renderButtons()}
      </DialogActions>
      {showPreviousFollowUpNoteConfirmDialog &&
        renderMarkPrevFollowUpNoteConfirmDialog()}
      {moreDetails && renderMoreNotesDetails()}
    </Dialog>
  );
};

export default WithPermission(
  AddCcmTimeDialog,
  AppPermissions.CCM.NAME,
  AppPermissions.CCM.CREATE
);
